import { useEffect, useState } from 'react';
import { useCapacitorSetup } from '@/hooks/platform';
import { useCapgoUpdates } from '@/hooks/platform';
import { useAnalytics } from '@/hooks/analytics';
import { useAuthInit } from '@/hooks/auth';
import { useTenant } from '@/hooks';
import { usePushNotificationInit } from './usePushNotificationInit';

interface InitializationState {
  capacitorReady: boolean;
  updatesReady: boolean;
  analyticsReady: boolean;
  authReady: boolean;
}

export const useAppInitialization = () => {
  const tenant = useTenant();
  const [initState, setInitState] = useState<InitializationState>({
    capacitorReady: false,
    updatesReady: false,
    analyticsReady: false,
    authReady: false,
  });

  // Use actual hooks for initialization
  const { isCapacitorReady } = useCapacitorSetup();
  const { isUpdatesReady } = useCapgoUpdates();
  const { isAnalyticsReady } = useAnalytics();
  const { isAuthReady } = useAuthInit({ tenant });

  // Auto-initialize push notifications for users who previously enabled them
  usePushNotificationInit();

  // Update initialization state based on actual hook states
  useEffect(() => {
    setInitState(prev => ({
      ...prev,
      capacitorReady: isCapacitorReady,
      updatesReady: isUpdatesReady,
      analyticsReady: isAnalyticsReady,
      authReady: isAuthReady,
    }));
  }, [isCapacitorReady, isUpdatesReady, isAnalyticsReady, isAuthReady]);

  // App is ready when auth is ready (critical path)
  const isAppReady = isAuthReady;

  // Non-critical features can load in background
  const isFullyReady = Object.values(initState).every(Boolean);

  return {
    isAppReady,
    isFullyReady,
    initState,
  };
}; 